import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  type InfiniteData,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { newsFeedItemCommentSchema, newsFeedItemSchema } from "../types";
import { NEWS_FEED_COMMENT_URL, NEWS_FEED_URL } from "./constants";
import { type NewsFeedItemsResponse } from "./useGetNewsFeedItems";

const createNewsFeedItemCommentRequestSchema = z.object({
  data: z.object({
    type: z.literal("news-feed-item-comment"),
    attributes: newsFeedItemCommentSchema.omit({ id: true, createdAt: true, workerId: true }),
  }),
});
export type CreateNewsFeedItemCommentRequest = z.infer<
  typeof createNewsFeedItemCommentRequestSchema
>;

export const updateNewsFeedItemResponseSchema = z.object({
  data: newsFeedItemSchema,
});
export type UpdateNewsFeedItemResponse = z.infer<typeof updateNewsFeedItemResponseSchema>;

export function useCreateNewsFeedItemComment(
  newsFeedItemId: string,
  options: UseMutationOptions<
    UpdateNewsFeedItemResponse,
    AxiosError,
    CreateNewsFeedItemCommentRequest
  > = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params) => {
      const response = await post({
        url: NEWS_FEED_COMMENT_URL.replace(":newsFeedItemId", newsFeedItemId),
        data: params,
        requestSchema: createNewsFeedItemCommentRequestSchema,
        responseSchema: updateNewsFeedItemResponseSchema,
      });

      return response.data;
    },
    ...options,
    onSuccess: async (data, variables, context) => {
      // optimistic update
      queryClient.setQueriesData<InfiniteData<NewsFeedItemsResponse>>(
        { queryKey: [NEWS_FEED_URL] },
        (existingData) => {
          if (!isDefined(existingData)) {
            return existingData;
          }

          const updatedItem = data.data;

          let existingPageWithItemIndex = -1;
          let existingItemWithinPageIndex = -1;

          existingData.pages.some((page, index) => {
            const indexWithinPage = page.data.findIndex((item) => item.id === data.data.id);

            if (indexWithinPage !== -1) {
              existingPageWithItemIndex = index;
              existingItemWithinPageIndex = indexWithinPage;
              return true;
            }

            return false;
          });

          if (existingPageWithItemIndex >= 0 && existingItemWithinPageIndex >= 0) {
            const existingPage = existingData.pages[existingPageWithItemIndex];

            if (!isDefined(existingPage)) {
              return existingData;
            }

            const updatedPage = {
              ...existingPage,
              data: [
                ...existingPage.data.slice(0, existingItemWithinPageIndex),
                updatedItem,
                ...existingPage.data.slice(existingItemWithinPageIndex + 1),
              ],
            };
            const existingPages = existingData.pages;

            const updatedPages = [
              ...existingPages.slice(0, existingPageWithItemIndex),
              updatedPage,
              ...existingPages.slice(existingItemWithinPageIndex + 1),
            ];

            return {
              ...existingData,
              pages: updatedPages,
            };
          }

          return existingData;
        }
      );

      void queryClient.invalidateQueries([NEWS_FEED_URL]);
      if (isDefined(options.onSuccess)) {
        options.onSuccess(data, variables, context);
      }
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.POST_NEWS_FEED_ITEM_COMMENT_FAILURE,
      ...options.meta,
    },
  });
}
